/**
 * +detail: ambient life on the open zone (presentation only, no sim units): butterflies over the
 * meadows by day, pollen in the air (fireflies at dusk and night), flocks of birds overhead, fish
 * jumping in the lake, rabbits and deer that bolt from the hero, and leaves drifting down from the
 * trees near the camera.
 *
 * Everything lives in small pools around the camera (one instanced draw per kind, eight in all),
 * respawned ahead as the camera moves and culled past a short distance; the CPU work is a few
 * hundred matrix updates a frame. Time of day comes from the atmosphere uniforms (ATMOS.uDay).
 */
import * as THREE from 'three';
import { U, ownedToonMaterial } from '../style';
import { ATMOS } from './atmosphere/uniforms';
import type { FieldLayout } from './layout';
import { combine, paint, xf } from './procs';

export interface FieldLife {
  group: THREE.Group;
  frame(camera: THREE.Camera, hero: { x: number; y: number } | null): void;
  setVisible(on: boolean): void;
  dispose(): void;
}

interface Water {
  level: number;
  lake: { c: { x: number; y: number }; rx: number; ry: number };
}

const TAU = Math.PI * 2;
const yawOf = (dx: number, dy: number): number => Math.atan2(dx, -dy);

/** Flapping-wing material (butterflies, birds) and static cards (leaves): unlit, dimmed at night. */
function wingMaterial(flap: number, amp: number, lift: number, side: THREE.Side = THREE.DoubleSide): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uDay: ATMOS.uDay, uFlap: { value: flap }, uAmp: { value: amp }, uLift: { value: lift } },
    vertexShader: `attribute float aPhase; attribute float aShade; uniform float uTime; uniform float uFlap; uniform float uAmp; uniform float uLift; varying vec3 vCol;
      void main(){
        vec3 p = position;
        float ax = abs(p.x);
        if (ax > 0.001 && uAmp > 0.0) {
          float a = sin(uTime * uFlap + aPhase) * uAmp + uLift;
          p.y += ax * sin(a);
          p.x = sign(position.x) * ax * cos(a);
        }
        vec3 col = vec3(1.0);
        #ifdef USE_INSTANCING_COLOR
          col = instanceColor;
        #endif
        vCol = col * aShade;
        gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: `uniform float uDay; varying vec3 vCol; void main(){ gl_FragColor = vec4(vCol * (0.3 + 0.7 * uDay), 1.0); }`,
    side,
  });
}

/** Wings: two quads hinged on the body axis (local x across, z along), plus a thin body. */
function wingGeometry(span: number, chord: number, tipChord: number, body: number): THREE.BufferGeometry {
  const pos: number[] = [];
  const shade: number[] = [];
  for (const s of [-1, 1]) {
    // root front, root back, tip back, tip front
    const rf = [0, 0, chord / 2];
    const rb = [0, 0, -chord / 2];
    const tb = [s * span, 0, -tipChord / 2 - chord * 0.15];
    const tf = [s * span, 0, tipChord / 2 - chord * 0.15];
    pos.push(...rf, ...rb, ...tb, ...rf, ...tb, ...tf);
    shade.push(0.75, 0.75, 1, 0.75, 1, 1);
  }
  // Body: a thin diamond along z (x = 0, so it never flaps).
  pos.push(0, 0.02, body / 2, 0, 0.02, -body / 2, 0, -0.03, 0);
  shade.push(0.35, 0.35, 0.35);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aShade', new THREE.Float32BufferAttribute(shade, 1));
  return g;
}

/** Butterfly: a broad fore wing and a rounder hind wing per side, a dark body. */
function butterflyGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const shade: number[] = [];
  const quad = (a: number[], b: number[], c: number[], d: number[], ka: number, kc: number): void => {
    pos.push(...a, ...b, ...c, ...a, ...c, ...d);
    shade.push(ka, ka, kc, ka, kc, kc);
  };
  for (const s of [-1, 1]) {
    quad([0, 0, 0.05], [0, 0, -0.01], [s * 0.2, 0, 0.02], [s * 0.17, 0, 0.13], 0.7, 1);
    quad([0, 0, -0.01], [0, 0, -0.07], [s * 0.1, 0, -0.14], [s * 0.15, 0, -0.04], 0.7, 0.92);
  }
  pos.push(0, 0.015, 0.07, 0, 0.015, -0.08, 0, -0.02, 0);
  shade.push(0.25, 0.25, 0.25);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aShade', new THREE.Float32BufferAttribute(shade, 1));
  return g;
}

/** Instanced mesh with a per-instance phase and colour. */
function pool(geo: THREE.BufferGeometry, mat: THREE.Material, n: number, name: string): THREE.InstancedMesh {
  const phase = new Float32Array(n);
  for (let i = 0; i < n; i++) phase[i] = Math.random() * TAU;
  geo.setAttribute('aPhase', new THREE.InstancedBufferAttribute(phase, 1));
  const im = new THREE.InstancedMesh(geo, mat, n);
  im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  im.frustumCulled = false;
  im.name = name;
  im.userData['noStyle'] = true;
  im.count = 0;
  return im;
}

const M4 = new THREE.Matrix4();
const Q = new THREE.Quaternion();
const E = new THREE.Euler();
const V = new THREE.Vector3();
const S = new THREE.Vector3();
const C = new THREE.Color();
const setAt = (im: THREE.InstancedMesh, i: number, x: number, y: number, z: number, yaw: number, pitch: number, roll: number, s: number): void => {
  Q.setFromEuler(E.set(pitch, yaw, roll, 'YXZ'));
  im.setMatrixAt(i, M4.compose(V.set(x, y, z), Q, S.setScalar(s)));
};

export function buildLife(L: FieldLayout, heightAt: (x: number, y: number) => number, water: Water | null): FieldLife {
  const group = new THREE.Group();
  group.name = 'field-life';
  const owned: { dispose(): void }[] = [];
  const rnd = Math.random;
  const lookId = (x: number, y: number): string => L.look(x, y).id;
  const walkable = (x: number, y: number): boolean => sampleSdf(L, x, y) < -1.5 && !L.blocked(x, y) && (!water || heightAt(x, y) > water.level + 0.15);

  // ---- butterflies (day, over flowers and meadows) ------------------------------------------
  const BUTTER = 28;
  const bMat = wingMaterial(14, 0.9, 0.35);
  const bGeo = butterflyGeometry();
  const butterflies = pool(bGeo, bMat, BUTTER, 'butterflies');
  owned.push(bGeo, bMat);
  group.add(butterflies);
  const BCOL = [0xfff2a0, 0xffffff, 0xf2a0c8, 0x8ab8ff, 0xffa040, 0xc8a0ff];
  for (let i = 0; i < BUTTER; i++) butterflies.setColorAt(i, C.setHex(BCOL[i % BCOL.length]!));
  type Fly = { x: number; y: number; h: number; tx: number; ty: number; hx: number; hy: number; alive: boolean; v: number };
  const flies: Fly[] = Array.from({ length: BUTTER }, () => ({ x: 0, y: 0, h: 1, tx: 0, ty: 0, hx: 0, hy: 0, alive: false, v: 1.6 }));

  // ---- birds (flocks overhead) -------------------------------------------------------------
  const FLOCKS = 3;
  const PER = 7;
  const birdMat = wingMaterial(9, 0.75, 0.15);
  const birdGeo = wingGeometry(0.6, 0.28, 0.1, 0.5);
  const birds = pool(birdGeo, birdMat, FLOCKS * PER, 'birds');
  owned.push(birdGeo, birdMat);
  group.add(birds);
  for (let i = 0; i < FLOCKS * PER; i++) birds.setColorAt(i, C.setHex(i % 3 ? 0x3a4250 : 0x4a4038));
  type Flock = { x: number; y: number; alt: number; dir: number; turn: number; alive: boolean; glide: number };
  const flocks: Flock[] = Array.from({ length: FLOCKS }, () => ({ x: 0, y: 0, alt: 30, dir: 0, turn: 0, alive: false, glide: 0 }));

  // ---- leaves drifting from the trees near the camera ----------------------------------------
  const LEAVES = 44;
  const leafMat = wingMaterial(0, 0, 0);
  const leafGeo = (() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0.09, -0.06, 0, 0, 0, 0, -0.09, 0, 0, 0.09, 0, 0, -0.09, 0.06, 0, 0], 3));
    g.setAttribute('aShade', new THREE.Float32BufferAttribute([1, 0.8, 1, 1, 1, 0.85], 1));
    return g;
  })();
  const leaves = pool(leafGeo, leafMat, LEAVES, 'leaves');
  owned.push(leafGeo, leafMat);
  group.add(leaves);
  const LCOL = [0x7fc04a, 0xa8cc50, 0xd8c050, 0x5fa548, 0xe0a040];
  for (let i = 0; i < LEAVES; i++) leaves.setColorAt(i, C.setHex(LCOL[i % LCOL.length]!));
  type Leaf = { x: number; y: number; h: number; ground: number; vy: number; spin: number; ph: number; alive: boolean };
  const leafs: Leaf[] = Array.from({ length: LEAVES }, () => ({ x: 0, y: 0, h: 0, ground: 0, vy: 0.7, spin: 1, ph: 0, alive: false }));
  // Tree crowns bucketed by 32 yd cell (sources for the leaves).
  type Crown = { x: number; y: number; h: number; r: number };
  const crowns = new Map<number, Crown[]>();
  const ckey = (ix: number, iy: number): number => (ix + 512) * 4096 + (iy + 512);
  for (const p of L.placements) {
    if (!/^(tree_|eldertree)/.test(p.id)) continue;
    const k = ckey(Math.floor(p.pos.x / 32), Math.floor(p.pos.y / 32));
    const l = crowns.get(k) ?? [];
    l.push({ x: p.pos.x, y: p.pos.y, h: p.h, r: Math.max(1.5, p.h * 0.32) });
    crowns.set(k, l);
  }
  const nearCrowns: Crown[] = [];
  let crownsAt = { x: Infinity, y: Infinity };

  // ---- critters: rabbits and deer that bolt from the hero ------------------------------------
  const critterMat = ownedToonMaterial({ color: 0xffffff, vertexColors: true });
  critterMat.name = 'field-critters';
  owned.push(critterMat);
  const rabbitGeo = rabbitGeometry();
  const deerGeo = deerGeometry();
  owned.push(rabbitGeo, deerGeo);
  const RABBITS = 9;
  const DEER = 4;
  const rabbits = new THREE.InstancedMesh(rabbitGeo, critterMat, RABBITS);
  const deer = new THREE.InstancedMesh(deerGeo, critterMat, DEER);
  for (const im of [rabbits, deer]) {
    im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    im.frustumCulled = false;
    im.castShadow = true;
    im.userData['noStyle'] = true;
    im.count = 0;
    group.add(im);
  }
  rabbits.name = 'rabbits';
  deer.name = 'deer';
  type Critter = { x: number; y: number; yaw: number; state: 'graze' | 'move' | 'flee'; t: number; dx: number; dy: number; speed: number; phase: number; alive: boolean; s: number };
  const mk = (): Critter => ({ x: 0, y: 0, yaw: 0, state: 'graze', t: 0, dx: 0, dy: 0, speed: 0, phase: 0, alive: false, s: 1 });
  const rab: Critter[] = Array.from({ length: RABBITS }, mk);
  const dee: Critter[] = Array.from({ length: DEER }, mk);
  const RABBIT_LAND = /^(meadow|dells|farm|lake|ruins|town)$/;
  const DEER_LAND = /^(meadow|forest|lake|dells|ruins)$/;

  // ---- fish jumping in the lake, splash rings --------------------------------------------------
  const fishMat = ownedToonMaterial({ color: 0xffffff, vertexColors: true });
  fishMat.name = 'field-fish';
  const fishGeo = combine([paint(xf(new THREE.SphereGeometry(0.16, 7, 5).scale(0.55, 0.8, 1.6), [0, 0, 0]), 0x8a9cb0, [0.7, 1.15]), paint(xf(new THREE.ConeGeometry(0.14, 0.2, 4).rotateX(-Math.PI / 2).scale(0.3, 1, 1), [0, 0, -0.3]), 0x6a7c90, [0.8, 1])]);
  owned.push(fishMat, fishGeo);
  const FISH = 2;
  const fish = new THREE.InstancedMesh(fishGeo, fishMat, FISH);
  fish.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  fish.frustumCulled = false;
  fish.userData['noStyle'] = true;
  fish.count = 0;
  fish.name = 'fish';
  group.add(fish);
  const RINGS = 6;
  const ringGeo = new THREE.RingGeometry(0.55, 0.75, 20).rotateX(-Math.PI / 2);
  const ringAge = new Float32Array(RINGS);
  ringGeo.setAttribute('aAge', new THREE.InstancedBufferAttribute(ringAge, 1));
  const ringMat = new THREE.ShaderMaterial({
    uniforms: { uDay: ATMOS.uDay },
    vertexShader: `attribute float aAge; varying float vA; void main(){ vA = aAge; gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform float uDay; varying float vA; void main(){ float a = (1.0 - vA) * 0.55; if (a <= 0.0) discard; gl_FragColor = vec4(vec3(0.85, 0.95, 1.0) * (0.4 + 0.6 * uDay), a); }`,
    transparent: true,
    depthWrite: false,
  });
  owned.push(ringGeo, ringMat);
  const rings = new THREE.InstancedMesh(ringGeo, ringMat, RINGS);
  rings.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  rings.frustumCulled = false;
  rings.userData['noStyle'] = true;
  rings.count = RINGS;
  rings.renderOrder = 2;
  rings.name = 'splash-rings';
  group.add(rings);
  type Jump = { x: number; y: number; dx: number; dy: number; t: number; alive: boolean };
  const jumps: Jump[] = Array.from({ length: FISH }, () => ({ x: 0, y: 0, dx: 0, dy: 0, t: 0, alive: false }));
  const ringState = Array.from({ length: RINGS }, () => ({ x: 0, y: 0, age: 1 }));
  let nextJump = 3;
  let ringNext = 0;
  const splash = (x: number, y: number): void => {
    const r = ringState[ringNext++ % RINGS]!;
    r.x = x;
    r.y = y;
    r.age = 0;
  };

  // ---- pollen by day, fireflies at dusk (one points field around the camera) -------------------
  const MOTES = 90;
  const mPos = new Float32Array(MOTES * 3);
  const mSeed = new Float32Array(MOTES);
  for (let i = 0; i < MOTES; i++) mSeed[i] = Math.random();
  const mGeo = new THREE.BufferGeometry();
  mGeo.setAttribute('position', new THREE.BufferAttribute(mPos, 3).setUsage(THREE.DynamicDrawUsage));
  mGeo.setAttribute('aSeed', new THREE.BufferAttribute(mSeed, 1));
  const mMat = new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uDay: ATMOS.uDay },
    vertexShader: `attribute float aSeed; uniform float uTime; uniform float uDay; varying float vA; varying float vNight;
      void main(){
        vNight = 1.0 - smoothstep(0.25, 0.7, uDay);
        float blink = 0.35 + 0.65 * pow(0.5 + 0.5 * sin(uTime * (1.3 + aSeed) + aSeed * 40.0), 3.0);
        vA = mix(0.55, blink, vNight);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = clamp(mix(0.07, 0.16, vNight) * 700.0 / max(1.0, -mv.z), 1.0, 14.0);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `varying float vA; varying float vNight;
      void main(){ vec2 d = gl_PointCoord * 2.0 - 1.0; float r = dot(d, d); if (r > 1.0) discard;
        vec3 c = mix(vec3(1.0, 0.97, 0.82), vec3(0.75, 1.0, 0.4) * 1.6, vNight);
        gl_FragColor = vec4(c * vA * (1.0 - r), 1.0); }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  owned.push(mGeo, mMat);
  const motes = new THREE.Points(mGeo, mMat);
  motes.frustumCulled = false;
  motes.userData['noStyle'] = true;
  motes.name = 'pollen-fireflies';
  group.add(motes);
  const moteBase = Array.from({ length: MOTES }, () => ({ x: (Math.random() - 0.5) * 60, y: (Math.random() - 0.5) * 60, h: 0.4 + Math.random() * 2.6 }));

  // ---- the loop ------------------------------------------------------------------------------
  let last = 0;
  let visible = true;
  const findSpot = (cx: number, cy: number, r0: number, r1: number, ok: (x: number, y: number) => boolean, tries = 10): { x: number; y: number } | null => {
    for (let k = 0; k < tries; k++) {
      const a = rnd() * TAU;
      const r = r0 + rnd() * (r1 - r0);
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (ok(x, y)) return { x, y };
    }
    return null;
  };

  function updateCritters(list: Critter[], im: THREE.InstancedMesh, deerKind: boolean, cx: number, cy: number, hero: { x: number; y: number } | null, dt: number, t: number): void {
    const land = deerKind ? DEER_LAND : RABBIT_LAND;
    const fleeR = deerKind ? 16 : 8;
    let n = 0;
    for (const c of list) {
      const dc = Math.hypot(c.x - cx, c.y - cy);
      if (!c.alive || dc > 95) {
        c.alive = false;
        // Spawn out of the hero's immediate view radius, on open ground of the right biome.
        const p = findSpot(cx, cy, 40, 80, (x, y) => land.test(lookId(x, y)) && walkable(x, y) && sampleGrid0(L, L.path, x, y) < 0.2 && (!hero || Math.hypot(x - hero.x, y - hero.y) > 30), 6);
        if (!p) continue;
        Object.assign(c, { x: p.x, y: p.y, yaw: rnd() * TAU, state: 'graze', t: 1 + rnd() * 4, alive: true, phase: rnd() * TAU, s: deerKind ? 0.9 + rnd() * 0.25 : 0.85 + rnd() * 0.35 });
      }
      // Bolt when the hero comes close.
      if (hero && c.state !== 'flee') {
        const dh = Math.hypot(c.x - hero.x, c.y - hero.y);
        if (dh < fleeR) {
          const a = Math.atan2(c.y - hero.y, c.x - hero.x) + (rnd() - 0.5) * 0.8;
          Object.assign(c, { state: 'flee', t: 2.2 + rnd() * 1.5, dx: Math.cos(a), dy: Math.sin(a), speed: deerKind ? 10 : 7.5 });
        }
      }
      c.t -= dt;
      if (c.t <= 0) {
        if (c.state === 'flee' || c.state === 'move' || rnd() < 0.4) Object.assign(c, { state: 'graze', t: 2 + rnd() * 5 });
        else {
          const a = rnd() * TAU;
          Object.assign(c, { state: 'move', t: 1 + rnd() * 2.5, dx: Math.cos(a), dy: Math.sin(a), speed: deerKind ? 1.4 : 2.2 });
        }
      }
      let moving = false;
      if (c.state !== 'graze') {
        const nx = c.x + c.dx * c.speed * dt;
        const ny = c.y + c.dy * c.speed * dt;
        if (walkable(nx, ny)) {
          c.x = nx;
          c.y = ny;
          moving = true;
          c.yaw = yawOf(c.dx, c.dy);
        } else {
          // Turn away from the obstacle / water.
          const a = Math.atan2(c.dy, c.dx) + (rnd() < 0.5 ? 1.6 : -1.6);
          c.dx = Math.cos(a);
          c.dy = Math.sin(a);
        }
      }
      const gy = heightAt(c.x, c.y);
      let bob = 0;
      let pitch = 0;
      if (moving) {
        c.phase += dt * (deerKind ? (c.state === 'flee' ? 9 : 4) : c.state === 'flee' ? 13 : 8);
        bob = Math.abs(Math.sin(c.phase)) * (deerKind ? (c.state === 'flee' ? 0.45 : 0.06) : 0.28);
        pitch = deerKind ? Math.cos(c.phase) * (c.state === 'flee' ? 0.12 : 0.02) : -Math.cos(c.phase) * 0.25;
      } else {
        // Grazing: head down now and then.
        pitch = deerKind ? (Math.sin(t * 0.7 + c.phase) > 0.3 ? 0.18 : 0) : Math.sin(t * 3 + c.phase) * 0.04;
      }
      setAt(im, n++, c.x, gy + bob, -c.y, c.yaw, pitch, 0, c.s);
    }
    im.count = n;
    im.instanceMatrix.needsUpdate = true;
  }

  const frame = (camera: THREE.Camera, hero: { x: number; y: number } | null): void => {
    if (!visible) return;
    const nowS = performance.now() / 1000;
    const dt = Math.min(0.1, last ? nowS - last : 0.016);
    last = nowS;
    const t = U.uTime.value as number;
    const cx = hero?.x ?? camera.position.x;
    const cy = hero?.y ?? -camera.position.z;
    const day = ATMOS.uDay.value;

    // Butterflies: wander between flowers near the camera by day.
    {
      let n = 0;
      for (let i = 0; i < flies.length; i++) {
        const f = flies[i]!;
        if (day < 0.35) {
          f.alive = false;
          continue;
        }
        if (!f.alive || Math.hypot(f.x - cx, f.y - cy) > 55) {
          const p = findSpot(cx, cy, 6, 45, (x, y) => /^(meadow|dells|farm|lake|ruins|town)$/.test(lookId(x, y)) && walkable(x, y), 4);
          if (!p) continue;
          Object.assign(f, { x: p.x, y: p.y, hx: p.x, hy: p.y, tx: p.x, ty: p.y, h: 0.6 + rnd(), alive: true, v: 1.2 + rnd() * 1.2 });
        }
        const dx = f.tx - f.x;
        const dy = f.ty - f.y;
        const d = Math.hypot(dx, dy);
        if (d < 0.3) {
          f.tx = f.hx + (rnd() - 0.5) * 9;
          f.ty = f.hy + (rnd() - 0.5) * 9;
        } else {
          f.x += (dx / d) * f.v * dt + Math.sin(t * 3 + i) * 0.4 * dt;
          f.y += (dy / d) * f.v * dt + Math.cos(t * 2.6 + i * 1.7) * 0.4 * dt;
        }
        const hy = heightAt(f.x, f.y) + f.h + Math.sin(t * 2.2 + i * 3.1) * 0.35;
        setAt(butterflies, n++, f.x, hy, -f.y, yawOf(dx, dy), 0, Math.sin(t * 5 + i) * 0.2, 1);
      }
      butterflies.count = n;
      butterflies.instanceMatrix.needsUpdate = true;
    }

    // Birds: flocks sweeping across the sky in loose Vs.
    {
      let n = 0;
      for (let k = 0; k < flocks.length; k++) {
        const f = flocks[k]!;
        if (day < 0.2) {
          f.alive = false;
          continue;
        }
        if (!f.alive || Math.hypot(f.x - cx, f.y - cy) > 230) {
          const a = rnd() * TAU;
          const r = 110 + rnd() * 70;
          Object.assign(f, { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, dir: a + Math.PI + (rnd() - 0.5) * 1.2, alt: 26 + rnd() * 22, turn: (rnd() - 0.5) * 0.12, alive: true, glide: rnd() * TAU });
        }
        f.dir += f.turn * dt;
        f.x += Math.cos(f.dir) * 9 * dt;
        f.y += Math.sin(f.dir) * 9 * dt;
        const base = heightAt(f.x, f.y) + f.alt;
        const fx = Math.cos(f.dir);
        const fy = Math.sin(f.dir);
        for (let b = 0; b < PER; b++) {
          const row = Math.ceil(b / 2);
          const side = b === 0 ? 0 : b % 2 ? 1 : -1;
          const back = row * 1.8;
          const lat = side * row * 1.5;
          const wob = Math.sin(t * 0.9 + b * 1.7 + k) * 0.5;
          const x = f.x - fx * back - fy * lat + wob * 0.3;
          const y = f.y - fy * back + fx * lat;
          setAt(birds, n++, x, base + wob - row * 0.3, -y, yawOf(fx, fy), 0, Math.sin(t + b) * 0.15, 1.9);
        }
      }
      birds.count = n;
      birds.instanceMatrix.needsUpdate = true;
    }

    // Leaves: fall from crowns near the camera.
    {
      if (Math.hypot(crownsAt.x - cx, crownsAt.y - cy) > 12) {
        crownsAt = { x: cx, y: cy };
        nearCrowns.length = 0;
        const ix = Math.floor(cx / 32);
        const iy = Math.floor(cy / 32);
        for (let i = ix - 1; i <= ix + 1; i++) for (let j = iy - 1; j <= iy + 1; j++) for (const c of crowns.get(ckey(i, j)) ?? []) if (Math.hypot(c.x - cx, c.y - cy) < 42) nearCrowns.push(c);
      }
      let n = 0;
      for (let i = 0; i < leafs.length; i++) {
        const l = leafs[i]!;
        if (!l.alive || l.h <= l.ground + 0.02 || Math.hypot(l.x - cx, l.y - cy) > 48) {
          l.alive = false;
          if (!nearCrowns.length || rnd() < 0.5) continue;
          const c = nearCrowns[Math.floor(rnd() * nearCrowns.length)]!;
          const a = rnd() * TAU;
          const r = rnd() * c.r;
          l.x = c.x + Math.cos(a) * r;
          l.y = c.y + Math.sin(a) * r;
          l.ground = heightAt(l.x, l.y);
          l.h = l.ground + c.h * (0.55 + rnd() * 0.3);
          l.vy = 0.5 + rnd() * 0.5;
          l.spin = (rnd() - 0.5) * 6;
          l.ph = rnd() * TAU;
          l.alive = true;
        }
        l.h -= l.vy * dt;
        l.x += (Math.sin(t * 1.3 + l.ph) * 0.6 + 0.35) * dt;
        l.y += Math.cos(t * 1.1 + l.ph) * 0.4 * dt;
        setAt(leaves, n++, l.x, Math.max(l.ground + 0.02, l.h), -l.y, t * l.spin + l.ph, Math.sin(t * 2 + l.ph) * 0.9, Math.cos(t * 1.7 + l.ph) * 0.9, 1.4);
      }
      leaves.count = n;
      leaves.instanceMatrix.needsUpdate = true;
    }

    // Critters.
    updateCritters(rab, rabbits, false, cx, cy, hero, dt, t);
    updateCritters(dee, deer, true, cx, cy, hero, dt, t);

    // Fish jumping (only near the lake).
    if (water) {
      const lc = water.lake.c;
      const nearLake = Math.hypot(cx - lc.x, cy - lc.y) < Math.max(water.lake.rx, water.lake.ry) + 110;
      nextJump -= dt;
      if (nearLake && nextJump <= 0) {
        nextJump = 2.5 + rnd() * 5;
        const j = jumps.find((q) => !q.alive);
        const p = findSpot(cx, cy, 10, 90, (x, y) => heightAt(x, y) < water.level - 1.3, 10);
        if (j && p) {
          const a = rnd() * TAU;
          Object.assign(j, { x: p.x, y: p.y, dx: Math.cos(a), dy: Math.sin(a), t: 0, alive: true });
          splash(p.x, p.y);
        }
      }
      let n = 0;
      for (const j of jumps) {
        if (!j.alive) continue;
        j.t += dt / 0.85;
        if (j.t >= 1) {
          j.alive = false;
          splash(j.x + j.dx * 1.6, j.y + j.dy * 1.6);
          continue;
        }
        const x = j.x + j.dx * 1.6 * j.t;
        const y = j.y + j.dy * 1.6 * j.t;
        const h = water.level + Math.sin(j.t * Math.PI) * 1.1 - 0.1;
        setAt(fish, n++, x, h, -y, yawOf(j.dx, j.dy), (j.t - 0.5) * 2.4, 0, 1.2);
      }
      fish.count = n;
      fish.instanceMatrix.needsUpdate = true;
      for (let i = 0; i < RINGS; i++) {
        const r = ringState[i]!;
        r.age = Math.min(1, r.age + dt / 1.1);
        ringAge[i] = r.age;
        setAt(rings, i, r.x, water.level + 0.04, -r.y, 0, 0, 0, r.age >= 1 ? 0 : 0.4 + r.age * 2.2);
      }
      (ringGeo.attributes['aAge'] as THREE.InstancedBufferAttribute).needsUpdate = true;
      rings.instanceMatrix.needsUpdate = true;
    }

    // Motes: a wrapped field around the camera (pollen by day, fireflies at night).
    for (let i = 0; i < MOTES; i++) {
      const b = moteBase[i]!;
      const s = mSeed[i]!;
      const wx = b.x + Math.sin(t * 0.3 + s * 20) * 2 + t * 0.4;
      const wy = b.y + Math.cos(t * 0.25 + s * 13) * 2 + t * 0.15;
      const x = cx + (((wx % 60) + 90) % 60) - 30;
      const y = cy + (((wy % 60) + 90) % 60) - 30;
      mPos[i * 3] = x;
      mPos[i * 3 + 1] = heightAt(x, y) + b.h + Math.sin(t * 0.8 + s * 30) * 0.3;
      mPos[i * 3 + 2] = -y;
    }
    (mGeo.attributes['position'] as THREE.BufferAttribute).needsUpdate = true;
    void day;
  };

  return {
    group,
    frame,
    setVisible(on: boolean) {
      visible = on;
      group.visible = on;
    },
    dispose() {
      group.removeFromParent();
      for (const im of [butterflies, birds, leaves, rabbits, deer, fish, rings]) im.dispose();
      for (const o of owned) o.dispose();
    },
  };
}

/** Signed distance to the walkable edge (negative inside), bilinear. */
function sampleSdf(L: FieldLayout, x: number, y: number): number {
  return sampleGrid0(L, L.sdf, x, y);
}
function sampleGrid0(L: FieldLayout, f: Float32Array, x: number, y: number): number {
  const g = L.grid;
  const i = Math.min(g.nx - 1, Math.max(0, Math.round((x - g.x0) / g.cell)));
  const j = Math.min(g.ny - 1, Math.max(0, Math.round((y - g.y0) / g.cell)));
  return f[j * g.nx + i]!;
}

/** A sitting rabbit (+z forward): round body, head, long ears, a white scut. */
function rabbitGeometry(): THREE.BufferGeometry {
  const fur = 0x9a8068;
  return combine([
    paint(xf(new THREE.SphereGeometry(0.2, 8, 6).scale(1, 0.85, 1.3), [0, 0.2, 0]), fur, [0.7, 1.08]),
    paint(xf(new THREE.SphereGeometry(0.12, 7, 5), [0, 0.36, 0.22]), fur, [0.8, 1.08]),
    paint(xf(new THREE.SphereGeometry(0.05, 5, 3).scale(0.6, 2.6, 0.4), [-0.05, 0.55, 0.18], [-0.3, 0, -0.15]), 0xb89a80, [0.8, 1.1]),
    paint(xf(new THREE.SphereGeometry(0.05, 5, 3).scale(0.6, 2.6, 0.4), [0.05, 0.55, 0.18], [-0.3, 0, 0.15]), 0xb89a80, [0.8, 1.1]),
    paint(xf(new THREE.SphereGeometry(0.07, 5, 4), [0, 0.22, -0.27]), 0xf4f0e8, [1, 1.05]),
    paint(xf(new THREE.SphereGeometry(0.02, 4, 3), [0.07, 0.39, 0.31]), 0x1a1410, [1, 1]),
    paint(xf(new THREE.SphereGeometry(0.02, 4, 3), [-0.07, 0.39, 0.31]), 0x1a1410, [1, 1]),
  ]);
}

/** A deer (+z forward): body, neck, head with ears, slender legs, a white tail. */
function deerGeometry(): THREE.BufferGeometry {
  const coat = 0xa86e44;
  const parts = [
    paint(xf(new THREE.SphereGeometry(0.32, 9, 6).scale(0.85, 0.9, 1.9), [0, 0.98, 0]), coat, [0.7, 1.08]),
    paint(xf(new THREE.CylinderGeometry(0.1, 0.15, 0.6, 6), [0, 1.3, 0.48], [0.55, 0, 0]), coat, [0.8, 1.05]),
    paint(xf(new THREE.SphereGeometry(0.13, 7, 5).scale(0.9, 0.9, 1.5), [0, 1.58, 0.68]), coat, [0.85, 1.08]),
    paint(xf(new THREE.ConeGeometry(0.06, 0.2, 4), [-0.09, 1.72, 0.6], [0, 0, 0.6]), 0x8a5a38, [0.9, 1]),
    paint(xf(new THREE.ConeGeometry(0.06, 0.2, 4), [0.09, 1.72, 0.6], [0, 0, -0.6]), 0x8a5a38, [0.9, 1]),
    paint(xf(new THREE.SphereGeometry(0.06, 5, 4), [0, 1.08, -0.6]), 0xf4ece0, [1, 1.05]),
    paint(xf(new THREE.SphereGeometry(0.03, 4, 3), [0, 1.56, 0.88]), 0x1a1410, [1, 1]),
  ];
  for (const [x, z] of [[-0.13, 0.35], [0.13, 0.35], [-0.13, -0.38], [0.13, -0.38]] as const) parts.push(paint(xf(new THREE.CylinderGeometry(0.035, 0.045, 0.75, 5).translate(0, 0.375, 0), [x, 0, z]), 0x7a5034, [0.6, 1]));
  return combine(parts);
}
